/**
 * StartPagePicker - выбор своей стартовой страницы в секции «Профиль».
 * Список - страницы интерфейса роли, на которые у роли хватает прав (available считает сервер).
 * Пустое значение - как у роли. Новых прав нет: выбирать можно только то, что роль и так открывает.
 */
import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, ListSubheader, MenuItem, Select, Typography } from '@mui/material';
import { ZERDE_COLORS } from '../constants';
import { notify } from '../../notifications';
import { apiErrorMessage } from '../../admin/reports/edit/editApi';
import { ProfileStartPage, saveStartPage, TargetInterface } from '../profileApi';
import { findStartPage, getStartPageOptions, StartPageOption } from '../../../config/startPages';

export interface StartPagePickerProps {
  targetInterface: TargetInterface;
  startPage: ProfileStartPage;
  onSaved: (next: ProfileStartPage) => void;
}

const ROLE_DEFAULT = '';

const StartPagePicker: React.FC<StartPagePickerProps> = ({ targetInterface, startPage, onSaved }) => {
  const saved = startPage.personal || ROLE_DEFAULT;
  const [value, setValue] = useState(saved);
  const [saving, setSaving] = useState(false);

  useEffect(() => setValue(saved), [saved]);

  const roleDefault = findStartPage(targetInterface, startPage.roleDefault);
  const groups: { name: string; items: StartPageOption[] }[] = [];
  getStartPageOptions(targetInterface).forEach(option => {
    if (startPage.available.indexOf(option.key) < 0) return;
    const group = groups.find(g => g.name === option.group);
    if (group) group.items.push(option);
    else groups.push({ name: option.group, items: [option] });
  });
  // Выбор, ставший недоступным роли, остается в списке неактивным: иначе Select показал бы пустое поле
  const lostChoice = saved && startPage.available.indexOf(saved) < 0 ? findStartPage(targetInterface, saved) : undefined;

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await saveStartPage(value || null);
      notify.success(result.message);
      onSaved(result.startPage);
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось сохранить стартовую страницу'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2
      }}
    >
      <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
          Сменить стартовую страницу
        </Typography>
        <Typography variant="caption" sx={{ color: lostChoice ? '#b45309' : '#64748b' }}>
          {lostChoice
            ? `«${lostChoice.label}» сейчас недоступна вашей роли - открывается страница роли`
            : 'Откроется после входа. В списке только разделы, доступные вашей роли'}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '1 1 300px', minWidth: 0 }}>
        <Select
          size="small"
          value={value}
          displayEmpty
          disabled={saving}
          onChange={e => setValue(String(e.target.value))}
          MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
          sx={{ flex: 1, minWidth: 0, fontSize: '0.875rem', borderRadius: '8px', bgcolor: '#ffffff' }}
        >
          <MenuItem value={ROLE_DEFAULT} sx={{ fontSize: '0.875rem' }}>
            Как у роли{roleDefault ? `: ${roleDefault.label}` : ''}
          </MenuItem>
          {lostChoice && (
            <MenuItem value={lostChoice.key} disabled sx={{ fontSize: '0.875rem' }}>
              {lostChoice.label} (недоступна)
            </MenuItem>
          )}
          {groups.map(group => [
            <ListSubheader key={`h-${group.name}`} sx={{ fontSize: '0.7rem', fontWeight: 700, lineHeight: '30px', color: '#94a3b8', textTransform: 'uppercase' }}>
              {group.name}
            </ListSubheader>,
            ...group.items.map(item => (
              <MenuItem key={item.key} value={item.key} sx={{ fontSize: '0.875rem' }}>
                {item.label}
              </MenuItem>
            ))
          ])}
        </Select>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSave}
          disabled={saving || value === saved}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{
            flexShrink: 0,
            bgcolor: ZERDE_COLORS.primary,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { bgcolor: '#1d4ed8' }
          }}
        >
          Сохранить
        </Button>
      </Box>
    </Box>
  );
};

export default StartPagePicker;
